/**
 * CreativesWidget - Виджет креативов с группировкой
 * ===================================================
 * 
 * Показывает производительность креативов с детальной статистикой
 * Поддерживает три режима группировки:
 * - По кампаниям (campaign)
 * - По креативам (creative)
 * - Без группировки (none)
 */

import React, { useState, useMemo, useEffect } from 'react';
import { BaseWidget, useBaseWidget } from './BaseWidget';
import { useWidgetData } from '../../hooks/useWidgetData';
import { MetricsService } from '../../services/metricsService';
import { imageCacheService } from '../../services/imageCacheService';
import { CampaignIcons } from '../ui/brand-icons';
import { CreativeViewerModal } from '../creatives/CreativeViewerModal';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';
import { 
  TrendingUp, 
  TrendingDown, 
  Minus,
  Search,
  DollarSign,
  Target,
  TrendingUp as TrendIcon,
  Zap,
  ChevronDown,
  ChevronRight,
  Video as VideoIcon,
  X
} from 'lucide-react';

// Map для отслеживания активных загрузок (предотвращает дубли)
const loadingThumbnails = new Map();

/**
 * Компонент превью креатива с кешированием thumbnail
 */
function CreativePreview({ preview, onClick }) {
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = React.useRef(null);
  
  useEffect(() => {
    if (!containerRef.current) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { rootMargin: '50px' }
    );
    
    observer.observe(containerRef.current);
    
    return () => observer.disconnect();
  }, []);
  
  useEffect(() => {
    if (!preview || !isVisible) return;
    
    const fileId = preview.webViewLink?.match(/\/d\/([^/]+)/)?.[1];
    if (!fileId) return;
    
    // URL держим в локальной переменной эффекта, а не читаем из state в cleanup:
    // замыкание видит значение на момент запуска эффекта (null на первом проходе),
    // поэтому revokeObjectURL по state не срабатывал никогда — blob'ы превью утекали.
    let objectUrl = null;
    let cancelled = false;

    loadThumbnail(fileId).then((url) => {
      if (cancelled) {
        if (url && url.startsWith('blob:')) URL.revokeObjectURL(url);
      } else {
        objectUrl = url;
      }
    });

    return () => {
      cancelled = true;
      if (objectUrl && objectUrl.startsWith('blob:')) {
        URL.revokeObjectURL(objectUrl);
      }
      setThumbnailUrl(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview, isVisible]);
  
  const loadThumbnail = async (fileId) => {
    const apiUrl = `/api/drive/thumbnail/${fileId}`;
    
    // Дожидаемся параллельной загрузки, но объектный URL создаём СВОЙ:
    // общий URL на два компонента означал бы, что revoke в одном ломает картинку в другом.
    if (loadingThumbnails.has(fileId)) {
      await loadingThumbnails.get(fileId);
      const cachedBlob = await imageCacheService.get(apiUrl);
      if (cachedBlob) {
        const ownUrl = URL.createObjectURL(cachedBlob);
        setThumbnailUrl(ownUrl);
        return ownUrl;
      }
      setThumbnailUrl(apiUrl);
      return apiUrl;
    }
    
    const loadPromise = (async () => {
      try {
        setIsLoading(true);
        
        const cachedBlob = await imageCacheService.get(apiUrl);
        
        if (cachedBlob) {
          const blobUrl = URL.createObjectURL(cachedBlob);
          setThumbnailUrl(blobUrl);
          setIsLoading(false);
          return blobUrl;
        }
        
        const response = await fetch(apiUrl);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        
        const blob = await response.blob();
        await imageCacheService.set(apiUrl, blob);
        
        const blobUrl = URL.createObjectURL(blob);
        setThumbnailUrl(blobUrl);
        setIsLoading(false);
        return blobUrl;
        
      } catch (err) {
        console.error('[CreativePreview] Ошибка загрузки thumbnail:', err);
        setIsLoading(false);
        setThumbnailUrl(apiUrl);
        return apiUrl;
      } finally {
        loadingThumbnails.delete(fileId);
      }
    })();
    
    loadingThumbnails.set(fileId, loadPromise);
    return loadPromise;
  };
  
  if (!preview) {
    return (
      <div className="w-12 h-12 bg-muted rounded flex items-center justify-center">
        <VideoIcon className="h-5 w-5 text-muted-foreground" />
      </div>
    );
  }
  
  const fileId = preview.webViewLink?.match(/\/d\/([^/]+)/)?.[1];
  
  if (!fileId) {
    return (
      <div className="w-12 h-12 bg-muted rounded flex items-center justify-center">
        <VideoIcon className="h-5 w-5 text-muted-foreground" />
      </div>
    );
  }
  
  return (
    <div 
      ref={containerRef}
      className="relative w-12 h-12 rounded overflow-hidden cursor-pointer hover:ring-2 hover:ring-primary transition-all"
      onClick={onClick}
      title={preview.fileName || 'Click to view'}
    >
      {isLoading ? (
        <div className="w-full h-full bg-muted flex items-center justify-center">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent" />
        </div>
      ) : thumbnailUrl ? (
        <img 
          src={thumbnailUrl}
          alt={preview.fileName || 'Creative thumbnail'}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full bg-muted flex items-center justify-center">
          <VideoIcon className="h-5 w-5 text-muted-foreground" />
        </div>
      )}
    </div>
  );
}

/**
 * Компонент тренда
 */
function TrendIndicator({ value, previousValue }) {
  if (!previousValue || previousValue === 0) {
    return <Minus className="h-4 w-4 text-muted-foreground" />;
  }
  
  const change = ((value - previousValue) / previousValue) * 100;
  
  if (Math.abs(change) < 1) {
    return <Minus className="h-4 w-4 text-muted-foreground" />;
  }
  
  const isPositive = change > 0;
  const Icon = isPositive ? TrendingUp : TrendingDown;
  const color = isPositive ? 'text-green-600' : 'text-red-600';
  
  return (
    <div className={`flex items-center gap-1 ${color}`}>
      <Icon className="h-4 w-4" />
      <span className="text-xs font-medium">
        {Math.abs(change).toFixed(1)}%
      </span>
    </div>
  );
}

/**
 * Компонент статистики
 */
function StatCard({ icon: Icon, label, value, subtitle, color = 'primary' }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 bg-${color}/10 rounded-lg`}>
            <Icon className={`h-5 w-5 text-${color}`} />
          </div>
          <div className="flex-1">
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="text-2xl font-bold">{value}</p>
            {subtitle && (
              <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Компонент строки креатива
 */
function CreativeRow({ creative, isGrouped = false, showCampaign = false, showPreview = true, onPreviewClick }) {
  const inapps = creative.revenue?.inapps || 0;
  const admon = creative.revenue?.admon || 0;
  const totalRevenue = inapps + admon;
  
  const cpi = MetricsService.calculateCPI(creative.cost, creative.installs);
  
  const roas = creative.roas || 0;
  const getStatus = () => {
    if (roas > 150) return { label: 'Excellent', variant: 'default' };
    if (roas > 100) return { label: 'Good', variant: 'secondary' };
    if (roas > 50) return { label: 'Warning', variant: 'outline' };
    return { label: 'Poor', variant: 'destructive' };
  };
  
  const status = getStatus();
  
  return (
    <TableRow className={isGrouped ? 'bg-muted/30' : ''}>
      <TableCell>
        {showPreview ? (
          <CreativePreview 
            preview={creative.preview} 
            onClick={() => onPreviewClick && onPreviewClick(creative)}
          />
        ) : (
          <div className="w-12 h-12" />
        )}
      </TableCell>
      
      <TableCell className="font-medium max-w-md">
        <div className="flex items-start gap-2">
          {isGrouped && <div className="w-6" />}
          <CampaignIcons 
            source={creative.mediaSource} 
            platform={creative.platform}
            size={16}
            gap={2}
          />
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm truncate" title={creative.adName}>
              {creative.adName}
            </p>
            {(!isGrouped || showCampaign) && (
              <p className="text-xs text-muted-foreground truncate">
                {creative.campaign}
              </p>
            )}
          </div>
        </div>
      </TableCell>
      
      <TableCell>
        <Badge variant={status.variant}>{status.label}</Badge>
      </TableCell>
      
      <TableCell className="text-right">
        <p className="font-medium">
          {MetricsService.formatNumber(creative.installs)}
        </p>
      </TableCell>
      
      <TableCell className="text-right">
        <p className="text-sm text-muted-foreground">
          {MetricsService.formatCurrency(cpi)}
        </p>
      </TableCell>
      
      <TableCell className="text-right">
        <div>
          <p className="font-medium">
            {MetricsService.formatCurrency(creative.cost)}
          </p>
          <TrendIndicator 
            value={creative.cost} 
            previousValue={creative.previousCost}
          />
        </div>
      </TableCell>
      
      <TableCell className="text-right">
        <p className="font-medium">
          {MetricsService.formatCurrency(inapps)}
        </p>
      </TableCell>
      
      <TableCell className="text-right">
        <p className="font-medium">
          {MetricsService.formatCurrency(admon)}
        </p>
      </TableCell>
      
      <TableCell className="text-right">
        <p className="font-medium">
          {MetricsService.formatCurrency(totalRevenue)}
        </p>
      </TableCell>
      
      <TableCell className="text-right">
        <p className={`font-medium ${roas >= 100 ? 'text-green-600' : 'text-amber-600'}`}>
          {roas.toFixed(1)}%
        </p>
      </TableCell>
    </TableRow>
  );
}

/**
 * Компонент группы креатива (по имени)
 */
function CreativeNameGroup({ creativeName, creatives, isExpanded, onToggle, sortConfig, onPreviewClick }) {
  const sortedCreatives = useMemo(() => {
    return [...creatives].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortConfig.key) {
        case 'campaign':
          aValue = a.campaign || '';
          bValue = b.campaign || '';
          return sortConfig.direction === 'asc' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        
        case 'installs':
          aValue = a.installs || 0;
          bValue = b.installs || 0;
          break;
        
        case 'cost':
          aValue = a.cost || 0;
          bValue = b.cost || 0;
          break;
        
        case 'revenue':
          aValue = (a.revenue?.inapps || 0) + (a.revenue?.admon || 0);
          bValue = (b.revenue?.inapps || 0) + (b.revenue?.admon || 0);
          break;
        
        case 'roas':
          aValue = a.roas || 0;
          bValue = b.roas || 0;
          break;
        
        default:
          return 0;
      }
      
      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });
  }, [creatives, sortConfig]);
  
  const totals = sortedCreatives.reduce((acc, creative) => {
    const inapps = creative.revenue?.inapps || 0;
    const admon = creative.revenue?.admon || 0;
    return {
      installs: acc.installs + creative.installs,
      cost: acc.cost + creative.cost,
      inapps: acc.inapps + inapps,
      admon: acc.admon + admon,
      revenue: acc.revenue + inapps + admon
    };
  }, { installs: 0, cost: 0, inapps: 0, admon: 0, revenue: 0 });
  
  const avgRoas = totals.cost > 0 ? (totals.revenue / totals.cost) * 100 : 0;
  const avgCpi = MetricsService.calculateCPI(totals.cost, totals.installs);
  const preview = creatives[0]?.preview;
  
  return (
    <>
      <TableRow className="bg-muted/50 hover:bg-muted/70 cursor-pointer" onClick={onToggle}>
        <TableCell>
          <CreativePreview 
            preview={preview} 
            onClick={(e) => {
              e.stopPropagation();
              onPreviewClick && onPreviewClick(creatives[0], creatives);
            }}
          />
        </TableCell>
        <TableCell className="font-semibold">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </Button>
            <span className="truncate" title={creativeName}>
              {creativeName}
            </span>
            <Badge variant="outline" className="ml-2">
              {creatives.length} campaigns
            </Badge>
          </div>
        </TableCell>
        <TableCell>
          <Badge variant={avgRoas >= 100 ? 'default' : 'outline'}>
            {avgRoas >= 100 ? 'Profitable' : 'Needs Work'}
          </Badge>
        </TableCell>
        <TableCell className="text-right">
          <p className="font-semibold">{MetricsService.formatNumber(totals.installs)}</p>
        </TableCell>
        <TableCell className="text-right">
          <p className="text-sm text-muted-foreground">{MetricsService.formatCurrency(avgCpi)}</p>
        </TableCell>
        <TableCell className="text-right">
          <p className="font-semibold">{MetricsService.formatCurrency(totals.cost)}</p>
        </TableCell>
        <TableCell className="text-right">
          <p className="font-semibold">{MetricsService.formatCurrency(totals.inapps)}</p>
        </TableCell>
        <TableCell className="text-right">
          <p className="font-semibold">{MetricsService.formatCurrency(totals.admon)}</p>
        </TableCell>
        <TableCell className="text-right">
          <p className="font-semibold">{MetricsService.formatCurrency(totals.revenue)}</p>
        </TableCell>
        <TableCell className="text-right">
          <p className={`font-semibold ${avgRoas >= 100 ? 'text-green-600' : 'text-amber-600'}`}>
            {avgRoas.toFixed(1)}%
          </p>
        </TableCell>
      </TableRow>
      
      {isExpanded && sortedCreatives.map((creative, index) => (
        <CreativeRow 
          key={`${creative.campaign}-${index}`} 
          creative={creative} 
          isGrouped={true}
          showCampaign={true}
          showPreview={false}
          onPreviewClick={onPreviewClick}
        />
      ))}
    </>
  );
}

/**
 * Компонент группы кампании
 */
function CampaignGroup({ campaign, creatives, isExpanded, onToggle, sortConfig, onPreviewClick }) {
  const sortedCreatives = useMemo(() => {
    return [...creatives].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortConfig.key) {
        case 'adName':
          aValue = a.adName || '';
          bValue = b.adName || '';
          return sortConfig.direction === 'asc' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        
        case 'installs':
          aValue = a.installs || 0;
          bValue = b.installs || 0;
          break;
        
        case 'cost':
          aValue = a.cost || 0;
          bValue = b.cost || 0;
          break;
        
        case 'revenue':
          aValue = (a.revenue?.inapps || 0) + (a.revenue?.admon || 0);
          bValue = (b.revenue?.inapps || 0) + (b.revenue?.admon || 0);
          break;
        
        case 'roas':
          aValue = a.roas || 0;
          bValue = b.roas || 0;
          break;
        
        default:
          return 0;
      }
      
      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });
  }, [creatives, sortConfig]);
  
  const totals = sortedCreatives.reduce((acc, creative) => {
    const inapps = creative.revenue?.inapps || 0;
    const admon = creative.revenue?.admon || 0;
    return {
      installs: acc.installs + creative.installs,
      cost: acc.cost + creative.cost,
      inapps: acc.inapps + inapps,
      admon: acc.admon + admon,
      revenue: acc.revenue + inapps + admon
    };
  }, { installs: 0, cost: 0, inapps: 0, admon: 0, revenue: 0 });
  
  const avgRoas = totals.cost > 0 ? (totals.revenue / totals.cost) * 100 : 0;
  const avgCpi = MetricsService.calculateCPI(totals.cost, totals.installs);
  
  return (
    <>
      <TableRow className="bg-muted/50 hover:bg-muted/70 cursor-pointer" onClick={onToggle}>
        <TableCell />
        <TableCell className="font-semibold">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </Button>
            <CampaignIcons 
              source={creatives[0]?.mediaSource} 
              platform={creatives[0]?.platform}
              size={16}
              gap={2}
            />
            <span className="truncate" title={campaign}>
              {campaign}
            </span>
            <Badge variant="outline" className="ml-2">
              {creatives.length} creatives
            </Badge>
          </div>
        </TableCell>
        <TableCell>
          <Badge variant={avgRoas >= 100 ? 'default' : 'outline'}>
            {avgRoas >= 100 ? 'Profitable' : 'Needs Work'}
          </Badge>
        </TableCell>
        <TableCell className="text-right">
          <p className="font-semibold">{MetricsService.formatNumber(totals.installs)}</p>
        </TableCell>
        <TableCell className="text-right">
          <p className="text-sm text-muted-foreground">{MetricsService.formatCurrency(avgCpi)}</p>
        </TableCell>
        <TableCell className="text-right">
          <p className="font-semibold">{MetricsService.formatCurrency(totals.cost)}</p>
        </TableCell>
        <TableCell className="text-right">
          <p className="font-semibold">{MetricsService.formatCurrency(totals.inapps)}</p>
        </TableCell>
        <TableCell className="text-right">
          <p className="font-semibold">{MetricsService.formatCurrency(totals.admon)}</p>
        </TableCell>
        <TableCell className="text-right">
          <p className="font-semibold">{MetricsService.formatCurrency(totals.revenue)}</p>
        </TableCell>
        <TableCell className="text-right">
          <p className={`font-semibold ${avgRoas >= 100 ? 'text-green-600' : 'text-amber-600'}`}>
            {avgRoas.toFixed(1)}%
          </p>
        </TableCell>
      </TableRow>
      
      {isExpanded && sortedCreatives.map((creative, index) => (
        <CreativeRow 
          key={`${creative.adName}-${index}`} 
          creative={creative} 
          isGrouped={true}
          onPreviewClick={onPreviewClick}
        />
      ))}
    </>
  );
}

/**
 * Компонент сводки
 */
function CreativesSummary({ creatives, serverTotals }) {
  // Копия: ниже поля inapps/admon дописываются, а serverTotals живёт в кэше виджета
  const totals = serverTotals ? { ...serverTotals } : creatives.reduce((acc, creative) => {
    const inapps = creative.revenue?.inapps || 0;
    const admon = creative.revenue?.admon || 0;
    return {
      uniqueCreatives: acc.uniqueCreatives + 1,
      installs: acc.installs + creative.installs,
      cost: acc.cost + creative.cost,
      inapps: acc.inapps + inapps,
      admon: acc.admon + admon,
      revenue: acc.revenue + inapps + admon
    };
  }, { uniqueCreatives: 0, installs: 0, cost: 0, inapps: 0, admon: 0, revenue: 0 });
  
  if (!totals.inapps && !totals.admon) {
    totals.inapps = creatives.reduce((sum, c) => sum + (c.revenue?.inapps || 0), 0);
    totals.admon = creatives.reduce((sum, c) => sum + (c.revenue?.admon || 0), 0);
    totals.revenue = totals.inapps + totals.admon;
  }
  
  const avgCPI = MetricsService.calculateCPI(totals.cost, totals.installs);
  const avgROAS = totals.cost > 0 ? (totals.revenue / totals.cost) * 100 : 0;
  
  const topCreative = creatives.reduce((best, current) => {
    return (current.roas || 0) > (best.roas || 0) ? current : best;
  }, creatives[0] || {});
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      <StatCard
        icon={Zap}
        label="Unique Creatives"
        value={totals.uniqueCreatives || creatives.length}
        subtitle={`${MetricsService.formatNumber(totals.installs)} installs`}
      />
      <StatCard
        icon={DollarSign}
        label="Total Cost"
        value={MetricsService.formatCurrency(totals.cost)}
        subtitle={`Avg CPI: ${MetricsService.formatCurrency(avgCPI)}`}
      />
      <StatCard
        icon={Target}
        label="Total Revenue"
        value={MetricsService.formatCurrency(totals.revenue)}
        subtitle={`ROAS: ${avgROAS.toFixed(1)}%`}
        color={avgROAS >= 100 ? 'green' : 'amber'}
      />
      <StatCard
        icon={TrendIcon}
        label="Top Creative"
        value={topCreative.roas ? `${topCreative.roas.toFixed(1)}%` : 'N/A'}
        subtitle={topCreative.adName ? topCreative.adName.substring(0, 30) + '...' : 'No data'}
      />
    </div>
  );
}

/**
 * Основной компонент виджета
 */
export function CreativesWidget() {
  const [searchQuery, setSearchQuery] = useState('');
  const [minInstalls, setMinInstalls] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'cost', direction: 'desc' });
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [groupingMode, setGroupingMode] = useState('campaign'); // 'campaign', 'creative', 'none'
  const [selectedCreative, setSelectedCreative] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const [shouldApplyFees, setShouldApplyFees] = React.useState(() => {
    const stored = localStorage.getItem('applyPlatformFees');
    return stored === 'true';
  });
  
  React.useEffect(() => {
    const handleStorageChange = () => {
      const stored = localStorage.getItem('applyPlatformFees');
      setShouldApplyFees(stored === 'true');
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('platformFeesChanged', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('platformFeesChanged', handleStorageChange);
    };
  }, []);
  
  const widgetData = useWidgetData(async (filters, options) => {
    const response = await MetricsService.getCreatives(filters, options);
    console.log('📊 Creatives API response:', {
      hasResponse: !!response,
      creativesCount: response?.creatives?.length,
      hasTotals: !!response?.totals,
      firstCreative: response?.creatives?.[0],
      firstCreativePreview: response?.creatives?.[0]?.preview
    });
    const creatives = response?.creatives || [];
    const totals = response?.totals || null;
    return { creatives, totals };
  });
  
  const widgetProps = useBaseWidget(widgetData);
  let { data } = widgetProps;
  
  let creatives = data?.creatives || data;
  let totals = data?.totals || null;
  
  // Чистый доход: revenue.inappsNet / roasNet / roiNet вместо gross, так что сортировка,
  // бейджи и группы идут по NET. У totals.revenue NET-пары нет — пересчитываем из строк.
  if (shouldApplyFees && creatives && Array.isArray(creatives)) {
    creatives = MetricsService.applyNetRevenue(creatives, true);
    if (totals) {
      totals = {
        ...MetricsService.applyNetRevenueToItem(totals),
        revenue: creatives.reduce(
          (sum, c) => sum + (c.revenue?.inapps || 0) + (c.revenue?.admon || 0), 0
        )
      };
    }
  }
  
  // Фильтрация по поиску и минимальным инсталам
  const filteredCreatives = useMemo(() => {
    if (!creatives || !Array.isArray(creatives)) return [];
    
    let filtered = creatives;
    
    // Фильтр по поиску
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(creative => 
        creative.adName?.toLowerCase().includes(query) ||
        creative.campaign?.toLowerCase().includes(query) ||
        creative.mediaSource?.toLowerCase().includes(query)
      );
    }
    
    // Фильтр по минимальным инсталам
    if (minInstalls) {
      const minValue = parseInt(minInstalls, 10);
      if (!isNaN(minValue)) {
        filtered = filtered.filter(creative => creative.installs >= minValue);
      }
    }
    
    return filtered;
  }, [creatives, searchQuery, minInstalls]);
  
  // Группировка по кампаниям
  const groupedByCampaign = useMemo(() => {
    if (!filteredCreatives || filteredCreatives.length === 0) return {};
    
    return filteredCreatives.reduce((groups, creative) => {
      const key = `${creative.mediaSource}|${creative.campaign}`;
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(creative);
      return groups;
    }, {});
  }, [filteredCreatives]);
  
  // Группировка по креативам
  const groupedByCreative = useMemo(() => {
    if (!filteredCreatives || filteredCreatives.length === 0) return {};
    
    return filteredCreatives.reduce((groups, creative) => {
      const key = creative.adName;
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(creative);
      return groups;
    }, {});
  }, [filteredCreatives]);
  
  // Сортировка
  const sortedData = useMemo(() => {
    if (!filteredCreatives || filteredCreatives.length === 0) return [];
    
    if (groupingMode === 'none') {
      // Обычная сортировка без группировки
      return [...filteredCreatives].sort((a, b) => {
        let aValue, bValue;
        
        switch (sortConfig.key) {
          case 'adName':
            aValue = a.adName || '';
            bValue = b.adName || '';
            return sortConfig.direction === 'asc' 
              ? aValue.localeCompare(bValue)
              : bValue.localeCompare(aValue);
          
          case 'installs':
            aValue = a.installs || 0;
            bValue = b.installs || 0;
            break;
          
          case 'cost':
            aValue = a.cost || 0;
            bValue = b.cost || 0;
            break;
          
          case 'revenue':
            aValue = (a.revenue?.inapps || 0) + (a.revenue?.admon || 0);
            bValue = (b.revenue?.inapps || 0) + (b.revenue?.admon || 0);
            break;
          
          case 'roas':
            aValue = a.roas || 0;
            bValue = b.roas || 0;
            break;
          
          default:
            return 0;
        }
        
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      });
    }
    
    // Сортировка групп (по кампаниям или креативам)
    const groupedData = groupingMode === 'campaign' ? groupedByCampaign : groupedByCreative;
    return Object.entries(groupedData).sort(([keyA, creativesA], [keyB, creativesB]) => {
      const totalsA = creativesA.reduce((acc, c) => ({
        installs: acc.installs + c.installs,
        cost: acc.cost + c.cost,
        revenue: acc.revenue + (c.revenue?.inapps || 0) + (c.revenue?.admon || 0)
      }), { installs: 0, cost: 0, revenue: 0 });
      
      const totalsB = creativesB.reduce((acc, c) => ({
        installs: acc.installs + c.installs,
        cost: acc.cost + c.cost,
        revenue: acc.revenue + (c.revenue?.inapps || 0) + (c.revenue?.admon || 0)
      }), { installs: 0, cost: 0, revenue: 0 });
      
      let aValue, bValue;
      
      switch (sortConfig.key) {
        case 'installs':
          aValue = totalsA.installs;
          bValue = totalsB.installs;
          break;
        case 'cost':
          aValue = totalsA.cost;
          bValue = totalsB.cost;
          break;
        case 'revenue':
          aValue = totalsA.revenue;
          bValue = totalsB.revenue;
          break;
        case 'roas':
          aValue = totalsA.cost > 0 ? (totalsA.revenue / totalsA.cost) * 100 : 0;
          bValue = totalsB.cost > 0 ? (totalsB.revenue / totalsB.cost) * 100 : 0;
          break;
        default:
          return 0;
      }
      
      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });
  }, [filteredCreatives, groupedByCampaign, groupedByCreative, sortConfig, groupingMode]);
  
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };
  
  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupKey)) {
        newSet.delete(groupKey);
      } else {
        newSet.add(groupKey);
      }
      return newSet;
    });
  };
  
  const expandAll = () => {
    const groupedData = groupingMode === 'campaign' ? groupedByCampaign : groupedByCreative;
    setExpandedGroups(new Set(Object.keys(groupedData)));
  };
  
  const collapseAll = () => {
    setExpandedGroups(new Set());
  };
  
  const handlePreviewClick = (creative, allVariants = null) => {
    setSelectedCreative({ creative, allVariants });
    setIsModalOpen(true);
  };
  
  return (
    <BaseWidget
      {...widgetProps}
      title="Creatives Performance"
      subtitle={totals ? `${totals.uniqueCreatives} unique creatives` : (creatives ? `${creatives.length} creatives` : null)}
      emptyMessage="No creatives data available"
      skeletonRows={5}
    >
      <div className="space-y-4">
        {sortedData && sortedData.length > 0 && (
          <CreativesSummary creatives={filteredCreatives} serverTotals={totals} />
        )}
        
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Поиск по названию, кампании, платформе..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-8"
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSearchQuery('')}
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 hover:bg-transparent"
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>
          <div className="relative w-48">
            <Input
              type="number"
              placeholder="Мин. инсталов..."
              value={minInstalls}
              onChange={(e) => setMinInstalls(e.target.value)}
              className="pr-8 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            />
            {minInstalls && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMinInstalls('')}
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 hover:bg-transparent"
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>
          {groupingMode !== 'none' && (
            <div className="flex gap-2 flex-shrink-0">
              <Button variant="outline" size="sm" onClick={expandAll}>
                Развернуть все
              </Button>
              <Button variant="outline" size="sm" onClick={collapseAll}>
                Свернуть все
              </Button>
            </div>
          )}
          <ToggleGroup type="single" value={groupingMode} onValueChange={setGroupingMode} className="flex-shrink-0">
            <ToggleGroupItem value="campaign" aria-label="Group by campaign">
              По кампаниям
            </ToggleGroupItem>
            <ToggleGroupItem value="creative" aria-label="Group by creative">
              По креативам
            </ToggleGroupItem>
            <ToggleGroupItem value="none" aria-label="No grouping">
              Без группировки
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
        
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Preview</TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('adName')}
                >
                  {groupingMode === 'campaign' ? 'Campaign / Creative' : groupingMode === 'creative' ? 'Creative / Campaign' : 'Creative Name'} {sortConfig.key === 'adName' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </TableHead>
                <TableHead>Status</TableHead>
                <TableHead 
                  className="text-right cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('installs')}
                >
                  Installs {sortConfig.key === 'installs' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </TableHead>
                <TableHead className="text-right">CPI</TableHead>
                <TableHead 
                  className="text-right cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('cost')}
                >
                  Cost {sortConfig.key === 'cost' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </TableHead>
                <TableHead className="text-right">Inapps</TableHead>
                <TableHead className="text-right">AdMon</TableHead>
                <TableHead 
                  className="text-right cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('revenue')}
                >
                  Revenue {sortConfig.key === 'revenue' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </TableHead>
                <TableHead 
                  className="text-right cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('roas')}
                >
                  ROAS {sortConfig.key === 'roas' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {groupingMode === 'campaign' ? (
                sortedData.map(([campaignKey, campaignCreatives]) => {
                  const campaign = campaignKey.split('|')[1];
                  return (
                    <CampaignGroup
                      key={campaignKey}
                      campaign={campaign}
                      creatives={campaignCreatives}
                      isExpanded={expandedGroups.has(campaignKey)}
                      onToggle={() => toggleGroup(campaignKey)}
                      sortConfig={sortConfig}
                      onPreviewClick={handlePreviewClick}
                    />
                  );
                })
              ) : groupingMode === 'creative' ? (
                sortedData.map(([creativeName, creativeVariants]) => {
                  return (
                    <CreativeNameGroup
                      key={creativeName}
                      creativeName={creativeName}
                      creatives={creativeVariants}
                      isExpanded={expandedGroups.has(creativeName)}
                      onToggle={() => toggleGroup(creativeName)}
                      sortConfig={sortConfig}
                      onPreviewClick={handlePreviewClick}
                    />
                  );
                })
              ) : (
                sortedData.map((creative, index) => (
                  <CreativeRow 
                    key={`${creative.adName}-${creative.campaign}-${index}`} 
                    creative={creative}
                    onPreviewClick={handlePreviewClick}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      
      <CreativeViewerModal
        creative={selectedCreative?.creative}
        allVariants={selectedCreative?.allVariants}
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </BaseWidget>
  );
}

export default CreativesWidget;
